import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { AuthProvider, useAuth } from '@/hooks/useAuth'
import { LoginPage } from '@/components/LoginPage'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { HomePage } from '@/pages/HomePage'
import { DeckPage } from '@/pages/DeckPage'
import { DocPage } from '@/pages/DocPage'
import { PreviewPage } from '@/pages/PreviewPage'
import '@/components/LoginPage.css'
import './App.css'

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <div className="loading-screen"><div className="loading-spinner" /></div>
  if (!user) return <LoginPage />
  return <>{children}</>
}

// Back-compat: the docs used to render as a modal at /deck/:slug/docs[/:docId].
// They now live on a dedicated page at /deck/:slug/doc[/:docId] — redirect old
// links (shared URLs, OG crawlers) to the page route.
function DocsRedirect() {
  const { slug, docId } = useParams<{ slug: string; docId: string }>()
  return <Navigate to={`/deck/${slug}/doc${docId ? `/${docId}` : ''}`} replace />
}

function AppRoutes() {
  return (
    <main>
    <Routes>
      <Route path="/deck/:slug" element={<AuthGuard><DeckPage /></AuthGuard>} />
      <Route path="/deck/:slug/doc" element={<AuthGuard><DocPage /></AuthGuard>} />
      <Route path="/deck/:slug/doc/:docId" element={<AuthGuard><DocPage /></AuthGuard>} />
      <Route path="/deck/:slug/docs" element={<DocsRedirect />} />
      <Route path="/deck/:slug/docs/:docId" element={<DocsRedirect />} />
      <Route path="/deck/:slug/:slide" element={<AuthGuard><DeckPage /></AuthGuard>} />
      <Route path="/preview/:slug" element={<PreviewPage />} />
      <Route path="/" element={<AuthGuard><HomePage /></AuthGuard>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </main>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  )
}
